/**
 * Screen Wake Lock manager — keeps the tablet screen awake during a visit.
 *
 * Why: prod logs (multi visit 2026-07-05, residence tablet) showed the failure
 * chain `web page is hidden` → Android suspends the tab's network → Chrome
 * OFFLINE → ~85s total connection loss mid-visit. The dominant trigger is the
 * screen auto-locking (30s–2min default timeout on tablets). Holding a screen
 * wake lock removes that trigger. It CANNOT prevent a manual lock or app
 * switch — for those we re-acquire when the page becomes visible again.
 *
 * Robustness rules (each one covers a real silent failure mode):
 * - The browser AUTO-RELEASES the lock every time the tab is hidden. A naive
 *   "acquire once on mount" dies at the first app switch. We re-acquire on
 *   every visibility→visible transition.
 * - Every wakeLock.request() returns a NEW WakeLockSentinel. The "release"
 *   listener must be attached on EVERY acquisition — attaching it once would
 *   silently stop logging/retaking after the first re-acquire.
 * - NotAllowedError (page hidden at request time, battery saver, restrictive
 *   Permissions-Policy or cross-origin iframe without allow="screen-wake-lock")
 *   must never throw — log and retry on the next visibility change.
 * - No API (old WebView): supported=false, everything no-ops.
 *
 * Pure logic with injectable deps so it is deterministic-testable without
 * rendering React (house pattern, cf. useStream tests / agora-*-client tests).
 */

export interface WakeLockSentinelLike {
  released: boolean;
  addEventListener(type: "release", cb: () => void): void;
  release(): Promise<void>;
}

export interface WakeLockManagerDeps {
  requestWakeLock?: () => Promise<WakeLockSentinelLike>;
  isVisible?: () => boolean;
}

export function createWakeLockManager(deps?: WakeLockManagerDeps) {
  const nav: any = typeof navigator !== "undefined" ? navigator : undefined;
  const request =
    deps?.requestWakeLock ??
    (nav?.wakeLock?.request
      ? () => nav.wakeLock.request("screen")
      : undefined);
  const isVisible =
    deps?.isVisible ??
    (() => typeof document !== "undefined" && document.visibilityState === "visible");

  let sentinel: WakeLockSentinelLike | null = null;
  let running = false;
  let acquiring = false;

  const supported = !!request;

  async function acquire(): Promise<void> {
    if (!running || !supported || acquiring) return;
    if (!isVisible()) return; // request would throw NotAllowedError while hidden
    if (sentinel && !sentinel.released) return; // already held
    acquiring = true;
    try {
      const s = await request!();
      if (!running) {
        // stopped while the request was in flight — don't keep the screen on
        s.release().catch(() => {});
        return;
      }
      sentinel = s;
      // NEW sentinel per request() → listener attached on EVERY acquisition.
      s.addEventListener("release", () => {
        console.log("[wake-lock] released", { visible: isVisible() });
        // Released while still visible and running = unexpected (not the
        // hidden-tab auto-release) → retake immediately.
        if (running && isVisible()) {
          void acquire();
        }
      });
      console.log("[wake-lock] acquired");
    } catch (err: any) {
      // Battery saver, hidden race, policy/iframe denial. Never throw; the
      // next visibilitychange retries. The log tells us WHY in Sentry
      // breadcrumbs (watch for policy denials from partner-portal iframes).
      console.warn("[wake-lock] denied", { name: err?.name, message: err?.message });
    } finally {
      acquiring = false;
    }
  }

  return {
    supported,

    start(): void {
      if (!supported) {
        console.log("[wake-lock] API unavailable — no-op");
        return;
      }
      if (running) return;
      running = true;
      void acquire();
    },

    async stop(): Promise<void> {
      running = false;
      const s = sentinel;
      sentinel = null;
      if (s && !s.released) {
        try {
          await s.release();
        } catch {
          /* already gone */
        }
        console.log("[wake-lock] released on stop");
      }
    },

    // Wire to document's visibilitychange: re-acquire when we come back.
    handleVisibilityChange(): void {
      if (running && isVisible()) {
        console.log("[wake-lock] page visible — re-acquiring");
        void acquire();
      }
    },
  };
}
